/* eslint-disable no-bitwise */
import * as React from 'react';
import { Avatar, AvatarOwnProps, Box, SxProps, Typography } from '@mui/material';
import { useAvatarUrl } from '@europrocurement/flexy-components/providers';
import { useUtilisateurAvatarUrl } from '@europrocurement/l2d-domain';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { sha256 } from '@europrocurement/l2d-utils';
import { useTheme } from '@mui/system';

function stringToColor(string: string): string {
    let hash = 0;
    let i: number;
    let color = '#';

    for (i = 0; i < string.length; i += 1) hash = string.charCodeAt(i) + ((hash << 5) - hash);
    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
}

export const extractInitials = function (_firstName?: string, _lastName?: string): string {
    let firstname = _firstName?.trim();
    let lastname = _lastName?.trim();

    if (firstname?.includes(' '))
        firstname = firstname
            ?.split(' ')
            .map((str) => str[0])
            .join('');
    if (lastname && lastname?.includes(' '))
        lastname = lastname
            ?.split(' ')
            .map((str) => str[0])
            .join('');

    if (firstname && !lastname) return (firstname[0] + (firstname[1] || '')).toUpperCase();
    if (firstname && lastname) return (firstname[0] + (lastname[0] || '')).toUpperCase();
    if (!firstname && lastname) return (lastname[0] + (lastname[1] || '')).toUpperCase();
    return '';
};

function stringAvatar(_firstName?: string, _lastName?: string, sx?: SxProps) {
    const children: string = extractInitials(_firstName, _lastName);

    return {
        sx: { ...sx, bgcolor: stringToColor(`${_firstName} ${_lastName}`) },
        children,
    };
}

export type CustomAvatarProps = {
    firstName?: string;
    lastName?: string;
    sx?: SxProps;
    src?: AvatarOwnProps['src'];
    badge?: '#4caf50' | '#9E9E9E' | '#F44336' | '#FF9800' | undefined;
    srcSet?: AvatarOwnProps['srcSet'];
    keycloakId?: string | null;
};

/**
 * @firstName user firstname
 * @lastName user lastname
 * @sx sx properties
 * @src image source url
 * @param keycloakId - preferred_username (identifiant keycloak) : give the keycloak userIdentifier to get the avatar of the user.
 * @returns CustomAvatar component
 * Note : for the current user avatar : give the keycloakId (userIdentifier) AND the avatarUrl as src to render the avatar modification
 */
export const CustomAvatar: React.FunctionComponent<CustomAvatarProps> = function ({
    firstName,
    lastName,
    sx,
    src,
    badge,
    keycloakId,
}: CustomAvatarProps) {
    const avatarBaseUrl = useUtilisateurAvatarUrl();
    const [hash, setHash] = React.useState<string | undefined>(undefined);
    const keycloak = UseKeycloakService();
    const { avatarUrl } = useAvatarUrl();
    const [avatarSrc, setAvatarSrc] = React.useState<string | undefined>(undefined);
    const [avatarImageSet, setAvatarImageSet] = React.useState<boolean>(false);
    const { palette } = useTheme();

    React.useEffect(() => {
        if (keycloakId && keycloakId !== '' && keycloakId !== null) {
            sha256(keycloakId).then(setHash);
        }
    }, [keycloakId, setHash]);

    React.useEffect(() => {
        if (src && src !== '') {
            setAvatarSrc(src);
        } else if (keycloakId) {
            if (
                keycloak.getTokenParsed().preferred_username === keycloakId &&
                avatarUrl !== undefined
            ) {
                setAvatarSrc(avatarUrl);
            } else if (hash) {
                setAvatarSrc(`${avatarBaseUrl}${hash}`);
            }
        }
    }, [src, keycloakId, avatarBaseUrl, hash, keycloak, avatarUrl]);

    React.useEffect(() => {
        if (keycloakId && keycloakId !== '' && keycloakId !== null) {
            sha256(keycloakId).then((newHash) => {
                if (newHash && newHash !== 'undefined' && newHash !== hash) {
                    setHash(newHash);
                }
            });
        }
    }, [hash, keycloakId, setHash]);

    // check if the url get an image to change avatar background style if true
    React.useEffect(() => {
        if (avatarSrc) {
            const check = async () => {
                await fetch(avatarSrc, { method: 'HEAD' }).then((res) => {
                    if (res.status === 200) {
                        setAvatarImageSet(true);
                    }
                });
            };
            check();
        }
    }, [avatarSrc]);

    return (
        <Box
            sx={{
                position: 'relative',
                width: sx?.width,
                height: sx?.height,
            }}
        >
            {avatarImageSet ? (
                <Avatar
                    src={avatarSrc}
                    sx={{ ...sx, backgroundColor: palette.background.paper }}
                    data-testid="CustomAvatar"
                />
            ) : (
                <Avatar
                    {...stringAvatar(firstName, lastName, {
                        ...sx,
                        fontSize: sx?.height ? `calc(${sx.height} * 0.55)` : undefined,
                    })}
                    src={avatarSrc}
                    data-testid="CustomAvatar"
                />
            )}
            {badge ? (
                <Typography
                    component="span"
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '12px',
                        height: '12px',
                        backgroundColor: badge,
                        color: badge,
                        border: '1px solid white',
                        borderRadius: '50%',
                    }}
                />
            ) : null}
        </Box>
    );
};
